import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  alpha,
  Grid,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';
import { Search, Close, Send } from '@mui/icons-material';
import { ClickAwayListener } from '@mui/material';
import chatApi from '../../services/chatApi';
import { Emoji } from '../../types/Chat';

interface EmojiPickerProps {
  open: boolean;
  onClose: () => void;
  onSelect: (emoji: string) => void;
  isDark: boolean;
}

const EmojiPicker: React.FC<EmojiPickerProps> = ({ open, onClose, onSelect, isDark }) => {
  const [emojis, setEmojis] = useState<Emoji[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedEmojis, setSelectedEmojis] = useState<string[]>([]);
  const [currentCategory, setCurrentCategory] = useState<string>('');

  const goldColor = '#C4B66F';
  const deepGreen = '#051814';

  // Glass effect styling
  const glassEffect = {
    backdropFilter: 'blur(20px)',
    background: isDark
      ? 'linear-gradient(135deg, rgba(5, 24, 20, 0.98) 0%, rgba(15, 59, 46, 0.98) 100%)'
      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(250, 250, 250, 0.98) 100%)',
    border: `1px solid ${alpha(isDark ? goldColor : deepGreen, 0.2)}`,
    boxShadow: isDark
      ? `0 25px 50px ${alpha('#000000', 0.5)}`
      : `0 25px 50px ${alpha(deepGreen, 0.15)}`,
  };

  // Emoji categories with single-word search terms for API
  const categories = [
    { label: '😀', name: 'Smileys', searchTerm: '' }, // Empty search term for all emojis
    { label: '❤️', name: 'Hearts', searchTerm: 'heart' },
    { label: '👍', name: 'Hands', searchTerm: 'hand' },
    { label: '🐻', name: 'Animals', searchTerm: 'animal' },
    { label: '🍕', name: 'Food', searchTerm: 'food' },
    { label: '⚽', name: 'Activities', searchTerm: 'sport' },
    { label: '🌍', name: 'Travel', searchTerm: 'travel' },
    { label: '💡', name: 'Objects', searchTerm: 'light' },
    { label: '💯', name: 'Symbols', searchTerm: 'symbol' },
    { label: '🎉', name: 'Celebration', searchTerm: 'party' },
  ];

  // Get fallback emojis if API fails
  const getFallbackEmojis = (): Emoji[] => {
    return [
      { character: '😀', slug: 'grinning-face', unicodeName: 'grinning face', group: 'Smileys & People', subGroup: 'face-positive' },
      { character: '😂', slug: 'face-with-tears-of-joy', unicodeName: 'face with tears of joy', group: 'Smileys & People', subGroup: 'face-positive' },
      { character: '🥰', slug: 'smiling-face-with-hearts', unicodeName: 'smiling face with hearts', group: 'Smileys & People', subGroup: 'face-affection' },
      { character: '😎', slug: 'smiling-face-with-sunglasses', unicodeName: 'smiling face with sunglasses', group: 'Smileys & People', subGroup: 'face-glasses' },
      { character: '🤔', slug: 'thinking-face', unicodeName: 'thinking face', group: 'Smileys & People', subGroup: 'face-neutral-skeptical' },
      { character: '😴', slug: 'sleeping-face', unicodeName: 'sleeping face', group: 'Smileys & People', subGroup: 'face-sleepy' },
      { character: '🥺', slug: 'pleading-face', unicodeName: 'pleading face', group: 'Smileys & People', subGroup: 'face-concerned' },
      { character: '😡', slug: 'pouting-face', unicodeName: 'pouting face', group: 'Smileys & People', subGroup: 'face-negative' },
      { character: '🤯', slug: 'exploding-head', unicodeName: 'exploding head', group: 'Smileys & People', subGroup: 'face-unwell' },
      { character: '🥳', slug: 'partying-face', unicodeName: 'partying face', group: 'Smileys & People', subGroup: 'face-hat' },
      { character: '❤️', slug: 'red-heart', unicodeName: 'red heart', group: 'Symbols', subGroup: 'heart' },
      { character: '👍', slug: 'thumbs-up', unicodeName: 'thumbs up', group: 'People & Body', subGroup: 'hand-fingers-closed' },
      { character: '👏', slug: 'clapping-hands', unicodeName: 'clapping hands', group: 'People & Body', subGroup: 'hands' },
      { character: '🔥', slug: 'fire', unicodeName: 'fire', group: 'Travel & Places', subGroup: 'sky & weather' },
      { character: '⭐', slug: 'star', unicodeName: 'star', group: 'Travel & Places', subGroup: 'sky & weather' },
      { character: '🎉', slug: 'party-popper', unicodeName: 'party popper', group: 'Activities', subGroup: 'event' },
      { character: '💯', slug: 'hundred-points', unicodeName: 'hundred points', group: 'Symbols', subGroup: 'alphanum' },
      { character: '✨', slug: 'sparkles', unicodeName: 'sparkles', group: 'Activities', subGroup: 'event' },
      { character: '🙏', slug: 'folded-hands', unicodeName: 'folded hands', group: 'People & Body', subGroup: 'hands' },
      { character: '😭', slug: 'loudly-crying-face', unicodeName: 'loudly crying face', group: 'Smileys & People', subGroup: 'face-concerned' },
    ];
  };

  const fetchEmojisByCategory = async (categoryIndex: number) => {
    const category = categories[categoryIndex];
    setLoading(true);
    setError(null);
    setCurrentCategory(category.name);
    
    try {
      if (categoryIndex === 0) {
        // First tab (Smileys) - fetch all emojis
        const response = await chatApi.searchEmojis({});
        if (response.success && response.emojis && response.emojis.length > 0) {
          setEmojis(response.emojis);
        } else {
          setEmojis(getFallbackEmojis());
        }
      } else {
        // Other tabs - fetch with specific search term
        const response = await chatApi.searchEmojis({ q: category.searchTerm });
        if (response.success && response.emojis && response.emojis.length > 0) {
          setEmojis(response.emojis);
        } else {
          setEmojis(getFallbackEmojis());
        }
      }
    } catch (err: any) {
      console.error('Error fetching emojis by category:', err);
      setError('Failed to load emojis');
      setEmojis(getFallbackEmojis());
    } finally {
      setLoading(false);
    }
  };

  const fetchEmojis = async (query?: string) => {
    setLoading(true);
    setError(null);
    setCurrentCategory('');
    
    try {
      const params = query ? { q: query } : {};
      const response = await chatApi.searchEmojis(params);
      
      if (response.success && response.emojis) {
        setEmojis(response.emojis);
      } else {
        setEmojis(getFallbackEmojis());
      }
    } catch (err: any) {
      console.error('Error fetching emojis:', err);
      setError('Failed to load emojis');
      setEmojis(getFallbackEmojis());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      // Load all emojis by default (first category - Smileys)
      fetchEmojisByCategory(0);
      setSelectedEmojis([]);
      setSelectedTab(0);
      setSearch('');
    }
  }, [open]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (open && search) {
        fetchEmojis(search);
        setSelectedTab(-1); // Clear tab selection when searching
        setCurrentCategory('');
      } else if (open && !search && selectedTab >= 0) {
        // If search is cleared and we have a selected tab, refetch that category
        fetchEmojisByCategory(selectedTab);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [search, open]);

  const handleEmojiClick = (emoji: string) => {
    setSelectedEmojis(prev => [...prev, emoji]);
    onSelect(emoji);
  };

  const handleSendSelected = () => {
    selectedEmojis.forEach(emoji => onSelect(emoji));
    setSelectedEmojis([]);
    onClose();
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
    setSearch(''); // Clear search when switching tabs
    fetchEmojisByCategory(newValue);
  };

  if (!open) return null;

  // Filter emojis based on search (only when search is active)
  const filteredEmojis = search
    ? emojis.filter(emoji => 
        emoji.unicodeName.toLowerCase().includes(search.toLowerCase()) ||
        emoji.slug.toLowerCase().includes(search.toLowerCase()) ||
        emoji.character.includes(search)
      )
    : emojis;

  return (
    <ClickAwayListener onClickAway={onClose}>
      <Paper
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: '100%',
          height: 400,
          ...glassEffect,
          borderRadius: '16px 16px 0 0',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 1400,
        }}
      >
        {/* Update Header to include close button */}
        <Box sx={{ 
          p: 2, 
          borderBottom: `1px solid ${alpha(isDark ? goldColor : deepGreen, 0.1)}`,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{ color: goldColor }}
          >
            <Close />
          </IconButton>
          <TextField
            fullWidth
            placeholder="Search emojis..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(5,24,20,0.02)',
                borderColor: alpha(isDark ? goldColor : deepGreen, 0.2),
                '&:hover': {
                  borderColor: goldColor,
                },
              },
              '& .MuiInputBase-input': {
                color: isDark ? '#fff' : deepGreen,
                fontSize: '0.9rem',
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: 20, color: goldColor }} />
                </InputAdornment>
              ),
              endAdornment: search && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearch('');
                      if (selectedTab >= 0) {
                        fetchEmojisByCategory(selectedTab);
                      }
                    }}
                    sx={{ color: goldColor }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Categories Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, overflowX: 'auto' }}>
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 40,
              '& .MuiTab-root': {
                minWidth: 40,
                minHeight: 40,
                padding: '4px 8px',
              },
            }}
          >
            {categories.map((category, index) => (
              <Tab
                key={category.name}
                icon={<span style={{ fontSize: '1.2rem' }}>{category.label}</span>}
                sx={{ minWidth: 40 }}
              />
            ))}
          </Tabs>
        </Box>

        {/* Selected Emojis Preview */}
        {selectedEmojis.length > 0 && (
          <Box sx={{ 
            p: 2, 
            borderBottom: `1px solid ${alpha(isDark ? goldColor : deepGreen, 0.1)}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: alpha(goldColor, 0.05),
          }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {selectedEmojis.map((emoji, index) => (
                <Box key={index} sx={{ fontSize: '1.5rem' }}>
                  {emoji}
                </Box>
              ))}
            </Box>
            <IconButton
              onClick={handleSendSelected}
              sx={{
                color: goldColor,
                bgcolor: alpha(goldColor, 0.1),
                '&:hover': {
                  bgcolor: alpha(goldColor, 0.2),
                },
              }}
            >
              <Send fontSize="small" />
            </IconButton>
          </Box>
        )}

        {/* Emoji Grid */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress sx={{ color: goldColor }} />
            </Box>
          ) : error ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography sx={{ color: isDark ? alpha('#fff', 0.7) : alpha(deepGreen, 0.7), mb: 2 }}>
                {error}
              </Typography>
              <Typography variant="body2" sx={{ color: isDark ? alpha('#fff', 0.5) : alpha(deepGreen, 0.5) }}>
                Using fallback emojis
              </Typography>
            </Box>
          ) : filteredEmojis.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography sx={{ color: isDark ? alpha('#fff', 0.7) : alpha(deepGreen, 0.7) }}>
                No emojis found
              </Typography>
            </Box>
          ) : (
            <>
              {/* Category indicator (only when not searching) */}
              {/* {!search && currentCategory && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: isDark ? alpha('#fff', 0.6) : alpha(deepGreen, 0.6),
                    display: 'block',
                    mb: 1,
                    textAlign: 'center'
                  }}
                >
                  {currentCategory === 'Smileys' ? 'All emojis' : `${currentCategory} emojis`}
                </Typography>
              )} */}
              
              <Grid container spacing={1}>
                {filteredEmojis.slice(0, 120).map((emoji, index) => (
                  <Grid 
                    size={{ xs: 2.4, sm: 2 }}
                    key={`${emoji.slug}-${index}`}
                  >
                    <IconButton
                      onClick={() => handleEmojiClick(emoji.character)}
                      sx={{
                        width: '100%',
                        height: 48,
                        borderRadius: '8px',
                        '&:hover': {
                          bgcolor: alpha(goldColor, 0.1),
                        },
                      }}
                    >
                      <Typography sx={{ fontSize: '1.5rem' }}>
                        {emoji.character}
                      </Typography>
                    </IconButton>
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </Box>
      </Paper>
    </ClickAwayListener>
  );
};

export default EmojiPicker;